import { defineStore } from 'pinia';
import { ref } from 'vue';
import {
  countryForCitizenship,
  type Citizenship,
  type CityItem,
  type CitySearchResponse,
  type CountryItem,
  type DictionaryFontWeight,
  type WorkObjectDisplayStyle,
  type StatusItem,
  TRAVEL_COLORS,
  businessTodayIso,
  nextTravelColor as nextAvailableTravelColor,
  type TravelGroupItem,
} from '@yana/shared';
import { api } from '@/api/client';

export interface SimpleItem {
  id: string;
  name: string;
  color?: string;
  textColor?: string | null;
  fontWeight?: DictionaryFontWeight | null;
  region?: string | null;
  selectLabel?: string | null;
  displayColor?: string | null;
  displayStyle?: WorkObjectDisplayStyle | null;
  qualifier?: string | null;
  isActive?: boolean;
}

export interface UserItem {
  id: string;
  name: string;
  username: string;
  role: string;
  color: string;
}

export interface EnumOption {
  value: string;
  label: string;
}

/**
 * Справочники.
 *
 * Небольшие (статусы, объекты, метки, сотрудники) грузятся целиком один раз.
 * Города — исключение: их сотни, и они всегда запрашиваются с сервера
 * по мере ввода, потому что каскад «страна → город» (решение Р-08) должен
 * работать на актуальном списке, а не на кэше месячной давности.
 */
export const useDictionariesStore = defineStore('dictionaries', () => {
  const countries = ref<CountryItem[]>([]);
  const statuses = ref<StatusItem[]>([]);
  const workObjects = ref<SimpleItem[]>([]);
  const sources = ref<SimpleItem[]>([]);
  const projects = ref<SimpleItem[]>([]);
  const tags = ref<SimpleItem[]>([]);
  const travelGroups = ref<TravelGroupItem[]>([]);
  const users = ref<UserItem[]>([]);
  const enums = ref<Record<string, EnumOption[]>>({ citizenship: [], gender: [] });

  const loaded = ref(false);
  let pending: Promise<void> | null = null;
  let generation = 0;

  function loadAll(): Promise<void> {
    if (loaded.value) return Promise.resolve();
    if (pending) return pending;
    const task = fetchAll(generation).finally(() => {
      if (pending === task) pending = null;
    });
    pending = task;
    return task;
  }

  async function fetchAll(request: number): Promise<void> {
    const [st, wo, so, pr, tg, tags_, us, en, co] = await Promise.all([
      api.get<{ items: StatusItem[] }>('/dictionaries/statuses'),
      api.get<{ items: SimpleItem[] }>('/dictionaries/work-objects'),
      api.get<{ items: SimpleItem[] }>('/dictionaries/sources'),
      api.get<{ items: SimpleItem[] }>('/dictionaries/projects'),
      api.get<{ items: TravelGroupItem[] }>('/dictionaries/travel-groups'),
      api.get<{ items: SimpleItem[] }>('/dictionaries/tags'),
      api.get<{ items: UserItem[] }>('/dictionaries/users'),
      api.get<Record<string, EnumOption[]>>('/dictionaries/enums'),
      api.get<{ items: CountryItem[] }>('/dictionaries/countries'),
    ]);

    if (request !== generation) return;
    statuses.value = st.items;
    workObjects.value = wo.items;
    sources.value = so.items;
    projects.value = pr.items;
    travelGroups.value = tg.items;
    tags.value = tags_.items;
    users.value = us.items;
    enums.value = en;
    countries.value = co.items;
    loaded.value = true;
  }

  /**
   * Сбросить кэш и перечитать. Нужен после правки справочников:
   * иначе редактор ячейки предлагает старые названия и цвета,
   * пока страницу не перезагрузят.
   */
  async function invalidate(): Promise<void> {
    generation += 1;
    pending = null;
    loaded.value = false;
    invalidateCities();
    await loadAll();
  }

  async function refreshTravelGroups(): Promise<void> {
    const tg = await api.get<{ items: TravelGroupItem[] }>('/dictionaries/travel-groups');
    travelGroups.value = tg.items;
  }

  async function refreshCountries(): Promise<void> {
    countries.value = (await api.get<{ items: CountryItem[] }>('/dictionaries/countries')).items;
  }

  function countryName(code: string): string {
    return countries.value.find((country) => country.code === code)?.name ?? code;
  }

  // ── Города: каскад «страна → город» (Р-08) ───────────────

  /**
   * Кэш поисковых запросов на время жизни страницы. Пользователь набирает
   * «мин» по одной букве — без кэша это три запроса на каждую ячейку.
   */
  const cityCache = new Map<string, CitySearchResponse>();
  let cityCacheGeneration = 0;

  /** После смены цвета города или общего цвета следующая выдача должна быть свежей. */
  function invalidateCities(): void {
    cityCacheGeneration += 1;
    cityCache.clear();
  }

  async function searchCitiesPage(
    query: string,
    citizenship: Citizenship | string | null | undefined,
    includeId?: string | null,
    offset = 0,
  ): Promise<CitySearchResponse> {
    const country = countryForCitizenship(citizenship);

    const key = `${country ?? '*'}|${query.toLowerCase()}|${includeId ?? ''}|${offset}`;
    const cached = cityCache.get(key);
    if (cached) return cached;

    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (country) params.set('country', country);
    if (includeId) params.set('includeId', includeId);
    params.set('limit', '20');
    params.set('offset', String(offset));

    const generation = cityCacheGeneration;
    const res = await api.get<CitySearchResponse>(`/dictionaries/cities?${params.toString()}`);
    // Старый запрос мог завершиться после смены общего цвета. Не сохраняем
    // его в кэш и не показываем устаревший цвет в открытом списке.
    if (generation !== cityCacheGeneration) {
      return searchCitiesPage(query, citizenship, includeId, offset);
    }
    cityCache.set(key, res);
    return res;
  }

  async function searchCities(
    query: string,
    citizenship: Citizenship | string | null | undefined,
    includeId?: string | null,
  ): Promise<CityItem[]> {
    return (await searchCitiesPage(query, citizenship, includeId)).items;
  }

  /** Название города по id — нужно, когда строка пришла без подстановки. */
  async function cityById(id: string): Promise<CityItem | null> {
    const res = await api.get<{ items: CityItem[] }>(
      `/dictionaries/cities?includeId=${id}&limit=1`,
    );
    return res.items.find((c) => c.id === id) ?? null;
  }

  /** Быстрое создание значения прямо из ячейки. */
  async function quickCreate(
    kind: 'cities' | 'travel-groups' | 'work-objects' | 'tags',
    payload: {
      name: string;
      country?: string;
      region?: string;
      departureDate?: string;
      color?: string;
    },
  ): Promise<SimpleItem> {
    const created = await api.post<SimpleItem>(`/dictionaries/${kind}`, payload);

    // Кэш городов после создания невалиден: только что созданный город
    // обязан находиться поиском немедленно
    if (kind === 'cities') invalidateCities();
    if (kind === 'work-objects') workObjects.value = [...workObjects.value, created];
    if (kind === 'tags') tags.value = [...tags.value, created];
    if (kind === 'travel-groups') await refreshTravelGroups();

    return created;
  }

  /**
   * Группа выезда заданного цвета — на сегодня.
   *
   * В рабочей таблице совместный выезд отмечали заливкой ячейки: залил
   * троих голубым — значит, едут одной машиной. Ни имён, ни списков.
   * Здесь то же движение: выбрал цвет — и люди в одной группе.
   *
   * Группе всё-таки нужно имя (по нему её потом находят в справочнике и
   * в фильтре), поэтому оно собирается само: «8 августа · голубые». В
   * пределах дня один цвет — одна группа: второй раз тем же цветом
   * добавляет людей к тем же, а не заводит вторую такую же.
   */
  async function travelGroupForColor(hex: string): Promise<SimpleItem> {
    const iso = businessTodayIso();
    const existing = travelGroups.value.find((g) => g.color === hex && g.departureDate === iso);
    if (existing) return existing;

    const day = new Date(`${iso}T00:00:00`).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
    });

    /*
      Имя собирается само: «9 августа · голубые».

      Цвету из палитры имя даёт цвет — так о группах и говорят вслух
      («голубые уехали»). Свой цвет из системной палитры названия не
      имеет, и «9 августа · #7f3fbf» никому ничего не скажет: такие
      группы нумеруются по порядку внутри дня.
    */
    const known = TRAVEL_COLORS.find((c) => c.hex === hex)?.label;
    const sameDay = travelGroups.value.filter((g) => g.departureDate === iso).length;
    const label = known ?? `группа ${sameDay + 1}`;

    return quickCreate('travel-groups', { name: `${day} · ${label}`, color: hex, departureDate: iso });
  }

  /**
   * Свободный цвет на сегодня.
   *
   * Цвет — не выбор человека, а способ различить группы глазом: когда
   * попутчиков набирают в карточке поимённо, спрашивать «каким цветом»
   * бессмысленно. Берём первый из палитры, который сегодня ещё не занят.
   *
   * Девять — не предел. Если все заняты, крутим оттенок по кругу с шагом,
   * который даёт заметно разные цвета: групп в день бывает и пятнадцать,
   * и отказывать в шестнадцатой из-за палитры нельзя.
   */
  function nextTravelColor(): string {
    const iso = businessTodayIso();
    return nextAvailableTravelColor(
      travelGroups.value.filter((g) => g.departureDate === iso).map((g) => g.color),
    );
  }

  /** Группа для попутчиков: цвет подбирается сам. */
  async function createTravelGroupAuto(): Promise<SimpleItem> {
    return travelGroupForColor(nextTravelColor());
  }

  function statusById(id: string | null): StatusItem | undefined {
    return id ? statuses.value.find((s) => s.id === id) : undefined;
  }

  function userById(id: string | null): UserItem | undefined {
    return id ? users.value.find((u) => u.id === id) : undefined;
  }

  return {
    countries,
    refreshCountries,
    countryName,
    statuses,
    workObjects,
    sources,
    projects,
    tags,
    travelGroups,
    users,
    enums,
    loaded,
    loadAll,
    invalidate,
    refreshTravelGroups,
    searchCities,
    searchCitiesPage,
    cityById,
    invalidateCities,
    quickCreate,
    travelGroupForColor,
    nextTravelColor,
    createTravelGroupAuto,
    statusById,
    userById,
  };
});
